import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, HelpCircle, Loader2, X, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { useAuth, API } from '../App';
import { toast } from 'sonner';

// ===============================================
// QUESTION BANK (27 questions, 4 sections)
// ===============================================
const SECTIONS = [
  { id: 'about', name: 'About You', intro: 'Let\'s start with a few questions about your background and what brings you to SPRING. There are no wrong answers.' },
  { id: 'mindset', name: 'Mindset & Motivation', intro: 'This section explores how you think about risk, resilience and what drives you. Be honest — we give better results when you are.' },
  { id: 'financial', name: 'Financial Situation', intro: 'Money matters. This section builds a clear picture of your financial position so we can give you honest, realistic guidance. Everything you enter stays private.' },
  { id: 'business', name: 'Business Idea & Skills', intro: 'The final section. Whether you have a clear idea or are starting from scratch, these questions help us understand what you bring to the table.' },
];

const QUESTIONS = [
  // Section 1: About You (Q1-Q5)
  { id: 'q1', section: 0, type: 'text', question: 'What is your name?', placeholder: 'Your full name', brian: null },
  { id: 'q2', section: 0, type: 'single', question: 'What is your current situation?',
    options: [
      { value: 'redundant', label: 'Recently made redundant' },
      { value: 'employed_exploring', label: 'Employed but exploring' },
      { value: 'self_employed', label: 'Self-employed / freelancing' },
      { value: 'between_jobs', label: 'Between jobs / searching' },
      { value: 'other', label: 'Other' },
    ],
    brian: "Not sure which to pick? Choose the one that feels most true right now." },
  { id: 'q3', section: 0, type: 'single', question: 'How long have you been in your primary career or profession?',
    options: [
      { value: 'under5', label: 'Under 5 years' },
      { value: '5to10', label: '5-10 years' },
      { value: '10to15', label: '10-15 years' },
      { value: '15to20', label: '15-20 years' },
      { value: '20plus', label: '20+ years' },
    ], brian: null },
  { id: 'q4', section: 0, type: 'single', question: 'Have you ever started a business or side project before — even informally?',
    options: [
      { value: 'ran_business', label: "Yes, I've run a business" },
      { value: 'side_project', label: 'Yes, a side project or freelance work' },
      { value: 'started_stopped', label: "I started something but it didn't go far" },
      { value: 'never', label: 'No, never' },
      { value: 'early_thinking', label: 'Just early-stage thinking' },
    ],
    brian: "Even informal or short-lived projects count — if you tried something, tell us." },
  { id: 'q5', section: 0, type: 'multi', question: 'What brings you to SPRING right now?', subtitle: 'Select all that apply',
    options: [
      { value: 'build_own', label: 'I want to build something of my own' },
      { value: 'redundant', label: 'I was made redundant / lost my job' },
      { value: 'unhappy_role', label: "I'm unhappy in my current role" },
      { value: 'specific_idea', label: 'I have a specific business idea I want to pursue' },
      { value: 'explore', label: 'I want to explore whether entrepreneurship is right for me' },
      { value: 'other', label: 'Other' },
    ],
    brian: "You can select more than one. There's no wrong reason to be here." },

  // Section 2: Mindset & Motivation (Q6-Q13)
  { id: 'q6', section: 1, type: 'scale5', question: 'I have a clear idea or problem I want to solve.',
    scaleLabels: { 1: 'Strongly disagree', 5: 'Strongly agree' },
    brian: "Think about whether you have a real, specific idea — not just a general interest in 'doing something.'" },
  { id: 'q7', section: 1, type: 'textarea', question: 'Describe a time you started something nobody asked you to start. What happened?',
    placeholder: 'It doesn\'t have to be a business — it could be a project at work, a community thing, something creative...',
    brian: "It doesn't have to be a business — it could be a project at work, a community thing, something creative. If you genuinely can't think of one, say so." },
  { id: 'q8', section: 1, type: 'scale5', question: 'When things get difficult, I keep going rather than stopping.',
    scaleLabels: { 1: 'Strongly disagree', 5: 'Strongly agree' }, brian: null },
  { id: 'q9', section: 1, type: 'textarea', question: 'Tell me about a time you failed at something. What happened and what did you do?',
    placeholder: 'Be honest — this is about what you learned, not about looking perfect...',
    brian: "This is one of the most important questions in the assessment. Honest answers about failure reveal more about your readiness than success stories do." },
  { id: 'q10', section: 1, type: 'single', question: "What's driving this more — wanting to escape something, or wanting to build something?",
    options: [
      { value: 'build', label: 'Mostly wanting to build something' },
      { value: 'mix', label: 'A genuine mix of both' },
      { value: 'escape', label: 'Mostly wanting to escape something' },
      { value: 'not_sure', label: "Honestly, I'm not sure yet" },
    ],
    brian: "There's no right answer here. This is about self-awareness, not about choosing the 'correct' option." },
  { id: 'q11', section: 1, type: 'scale5', question: "How comfortable are you with uncertainty and not knowing what comes next?",
    scaleLabels: { 1: 'Very uncomfortable', 5: 'Very comfortable' },
    brian: "Think about how you've actually reacted in uncertain situations before — not how you'd like to react." },
  { id: 'q12', section: 1, type: 'scale10', question: "How much do you genuinely WANT to do something different?",
    subtitle: "Not 'should' — truly WANT.",
    scaleLabels: { 1: 'Not really', 10: 'Absolutely' },
    brian: "Be honest. A 6 is not a failure — it gives us important information." },
  { id: 'q13', section: 1, type: 'textarea', question: "What does success look like for you in 2-3 years if this works out?",
    placeholder: "Be specific. What does your day-to-day look like? What does it give you?",
    brian: "Be specific. 'Successful business' is not specific enough — what does it look like day to day?" },

  // Section 3: Financial Situation (Q14-Q21)
  { id: 'q14', section: 2, type: 'single', question: 'My current financial situation feels like:',
    options: [
      { value: 'secure', label: 'Financially secure (12+ months without income)' },
      { value: 'stable', label: 'Relatively stable (6-12 months)' },
      { value: 'getting_by', label: 'Getting by (3-6 months)' },
      { value: 'tight', label: 'Tight (1-3 months)' },
      { value: 'pressure', label: 'Under pressure (<1 month)' },
    ],
    brian: "This is a gut-check before the numbers. The next questions collect the actual data." },
  { id: 'q15', section: 2, type: 'currency', question: 'Monthly income (all sources combined)',
    placeholder: '0', brian: "Enter your total household monthly income — include your partner's income if it supports your living expenses." },
  { id: 'q16', section: 2, type: 'currency', question: 'Monthly essential expenses',
    placeholder: '0', brian: "Essential expenses only — not discretionary spending. If you're not sure, estimate conservatively." },
  { id: 'q17', section: 2, type: 'currency', question: 'Total liquid savings and accessible assets',
    placeholder: '0', brian: "Only include money you could access within 30 days if needed." },
  { id: 'q18', section: 2, type: 'currency', question: 'Total debt (excluding mortgage)',
    placeholder: '0', brian: "Include credit cards, personal loans, car loans, student loans. Exclude your mortgage." },
  { id: 'q19', section: 2, type: 'housing', question: 'Do you have a mortgage or rent payment?', brian: null },
  { id: 'q20', section: 2, type: 'single', question: 'Do you have access to startup capital (money specifically for a business)?',
    options: [
      { value: 'yes_saved', label: 'Yes, I have it saved' },
      { value: 'family_partner', label: 'Family or partner could help' },
      { value: 'could_borrow', label: 'Could borrow or use credit' },
      { value: 'not_sure', label: "I'm not sure" },
      { value: 'not_available', label: 'Not available' },
    ],
    brian: "Startup capital is separate from your emergency fund." },
  { id: 'q21', section: 2, type: 'single', question: 'Starting a business while managing my current financial obligations would be:',
    options: [
      { value: 'easy', label: 'Easy — no financial pressure' },
      { value: 'manageable', label: 'Manageable — tight but doable' },
      { value: 'challenging', label: 'Challenging — real pressure' },
      { value: 'difficult', label: 'Very difficult — significant strain' },
      { value: 'impossible', label: 'Probably impossible right now' },
    ],
    brian: "Be honest — this is about your actual life, not about looking determined." },

  // Section 4: Business Idea & Skills (Q22-Q27)
  { id: 'q22', section: 3, type: 'textarea', question: 'Describe your business idea in 2-3 sentences.',
    subtitle: 'What is it, who would pay for it, and why would they choose it over alternatives?',
    placeholder: "If you don't have a specific idea yet, describe the problem or industry you're drawn to...",
    brian: "If you don't have a specific idea yet, describe the problem or industry you're drawn to instead. That's a perfectly valid starting point." },
  { id: 'q23', section: 3, type: 'single', question: 'Have you spoken to any potential customers about this idea?',
    options: [
      { value: '10plus', label: 'Yes, 10+ conversations' },
      { value: '5to9', label: 'Yes, 5-9 conversations' },
      { value: '1to4', label: 'Yes, 1-4 conversations' },
      { value: 'plan_to', label: "Not yet, but I plan to" },
      { value: 'no', label: "No, and I'm not sure if I will" },
    ],
    brian: "This is about real conversations — not just mentioning it to friends or family." },
  { id: 'q24', section: 3, type: 'single', question: 'Do you have relevant professional experience in this industry or field?',
    options: [
      { value: '10plus', label: '10+ years direct experience' },
      { value: '5to10', label: '5-10 years' },
      { value: '1to5', label: '1-5 years' },
      { value: 'indirect', label: 'Some indirect exposure' },
      { value: 'none', label: "No, but I'm willing to learn" },
    ], brian: null },
  { id: 'q25', section: 3, type: 'skill_grid', question: 'Rate your current skill level in each area:',
    skills: ['Sales & Marketing', 'Financial Management', 'Technology & Digital Tools', 'Project Management'],
    brian: "Rate honestly — not where you think you should be, where you actually are." },
  { id: 'q26', section: 3, type: 'textarea', question: "What keeps you up at night about this — what would make you quit?",
    placeholder: "This is about self-awareness, not commitment...",
    brian: "This question is about self-awareness, not commitment. Honest answers help us give you better results." },
  { id: 'q27', section: 3, type: 'scale5', question: 'I can work independently without constant direction or external approval.',
    scaleLabels: { 1: 'Strongly disagree', 5: 'Strongly agree' }, brian: null },
];

// ===============================================
// FINANCIAL CALCULATOR COMPONENT
// ===============================================
const FinancialCalculator = ({ answers }) => {
  const income = parseFloat(answers.q15 || 0);
  const expenses = parseFloat(answers.q16 || 0);
  const savings = parseFloat(answers.q17 || 0);

  if (!income && !expenses && !savings) return null;

  const surplus = income - expenses;
  const burnRate = expenses || 1;
  const runway = savings / burnRate;
  const runwayDisplay = runway > 24 ? '24+' : Math.max(0, runway).toFixed(1);

  const getColour = (months) => {
    if (months >= 12) return { bg: '#dcfce7', text: '#166534', label: 'Strong' };
    if (months >= 3) return { bg: '#fef9c3', text: '#854d0e', label: 'Manageable' };
    return { bg: '#fecaca', text: '#991b1b', label: 'Concern' };
  };

  const col = getColour(runway);

  return (
    <div style={{
      background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 0,
      padding: '24px', marginTop: '20px',
    }} data-testid="financial-calculator">
      <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#64748b', marginBottom: '16px' }}>
        Live Financial Summary
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
        <div style={{ background: '#fff', borderRadius: 0, padding: '14px', border: '1px solid #e2e8f0' }}>
          <p style={{ fontSize: '11px', color: '#94a3b8' }}>Monthly Surplus</p>
          <p style={{ fontSize: '20px', fontWeight: 700, color: surplus >= 0 ? '#166534' : '#991b1b' }}>
            ${Math.abs(surplus).toLocaleString()}
            <span style={{ fontSize: '12px', fontWeight: 400, marginLeft: '4px' }}>{surplus >= 0 ? 'surplus' : 'deficit'}</span>
          </p>
        </div>
        <div style={{ background: '#fff', borderRadius: 0, padding: '14px', border: '1px solid #e2e8f0' }}>
          <p style={{ fontSize: '11px', color: '#94a3b8' }}>Monthly Burn Rate</p>
          <p style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>${burnRate.toLocaleString()}</p>
        </div>
      </div>
      <div style={{ background: col.bg, borderRadius: 0, padding: '16px', textAlign: 'center' }}>
        <p style={{ fontSize: '11px', color: col.text, opacity: 0.7, marginBottom: '4px' }}>Runway</p>
        <p style={{ fontSize: '28px', fontWeight: 800, color: col.text }}>{runwayDisplay} months</p>
        <p style={{ fontSize: '12px', color: col.text, opacity: 0.7 }}>{col.label}</p>
      </div>
    </div>
  );
};

// ===============================================
// MAIN COMPONENT
// ===============================================
const Stage1Assessment = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [screen, setScreen] = useState('welcome'); // welcome | section-intro | question | processing | done
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showBrian, setShowBrian] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [processingStep, setProcessingStep] = useState(0);

  // Load saved progress
  useEffect(() => {
    const loadProgress = async () => {
      try {
        const res = await axios.get(`${API}/assessment/stage1/progress`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.status === 'completed') {
          navigate('/portal/stages');
          return;
        }
        if (res.data.answers && Object.keys(res.data.answers).length > 0) {
          setAnswers(res.data.answers);
          setCurrentQ(res.data.current_question || 0);
          toast('Welcome back — we saved your progress.');
        }
      } catch (e) { /* first time */ }
      setLoading(false);
    };
    loadProgress();
  }, [token, navigate]);

  // Auto-save after each answer
  const saveProgress = useCallback(async (newAnswers, qIndex) => {
    try {
      await axios.post(`${API}/assessment/stage1/save`, {
        answers: newAnswers, current_question: qIndex,
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) { /* silent */ }
  }, [token]);

  const currentQuestion = QUESTIONS[currentQ];
  const currentSection = currentQuestion ? SECTIONS[currentQuestion.section] : null;
  const progress = QUESTIONS.length > 0 ? Math.round((currentQ / QUESTIONS.length) * 100) : 0;

  // Check if we need to show a section intro
  const prevQuestion = currentQ > 0 ? QUESTIONS[currentQ - 1] : null;
  const isNewSection = !prevQuestion || (currentQuestion && prevQuestion.section !== currentQuestion.section);

  const setAnswer = (key, value) => {
    const newAnswers = { ...answers, [key]: value };
    setAnswers(newAnswers);
  };

  const handleNext = () => {
    setShowBrian(false);
    const newAnswers = { ...answers };
    saveProgress(newAnswers, currentQ + 1);

    if (currentQ < QUESTIONS.length - 1) {
      setCurrentQ(currentQ + 1);
      setScreen('question');
    } else {
      handleSubmit(newAnswers);
    }
  };

  const handleBack = () => {
    setShowBrian(false);
    if (currentQ > 0) {
      setCurrentQ(currentQ - 1);
      setScreen('question');
    } else {
      setScreen('welcome');
    }
  };

  const handleSubmit = async (finalAnswers) => {
    setScreen('processing');
    setSubmitting(true);

    // Animated processing steps
    const steps = [
      'Analysing your responses...',
      'Building your financial picture...',
      'Evaluating mindset indicators...',
      'Running the 3-Pillar check...',
      'Calculating your Founder DNA profile...',
      'Assigning your pathway...',
    ];

    for (let i = 0; i < steps.length; i++) {
      setProcessingStep(i);
      await new Promise(r => setTimeout(r, 1200));
    }

    try {
      await axios.post(`${API}/assessment/stage1/complete`, {
        answers: finalAnswers,
      }, { headers: { Authorization: `Bearer ${token}` } });

      setScreen('done');
    } catch (e) {
      toast.error('Something went wrong. Please try again.');
      setScreen('question');
    }
    setSubmitting(false);
  };

  const canProceed = () => {
    if (!currentQuestion) return false;
    const val = answers[currentQuestion.id];
    switch (currentQuestion.type) {
      case 'text': return val && val.trim().length > 0;
      case 'textarea': return val && val.trim().length > 10;
      case 'single': return !!val;
      case 'multi': return Array.isArray(val) && val.length > 0;
      case 'scale5': return val !== undefined && val !== null;
      case 'scale10': return val !== undefined && val !== null;
      case 'currency': return val !== undefined && val !== '' && val !== null;
      case 'housing': return answers[`${currentQuestion.id}_type`] !== undefined;
      case 'skill_grid': return val && Object.keys(val).length === currentQuestion.skills.length;
      default: return true;
    }
  };

  const processingSteps = [
    'Analysing your responses...',
    'Building your financial picture...',
    'Evaluating mindset indicators...',
    'Running the 3-Pillar check...',
    'Calculating your Founder DNA profile...',
    'Assigning your pathway...',
  ];

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F6ADBC', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#1E491A' }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F6ADBC', fontFamily: 'Arial, sans-serif', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <span style={{ fontSize: '22px', fontWeight: 800, color: '#1E491A', letterSpacing: '-0.5px' }}>SPRING.</span>
        {screen === 'question' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '12px', color: '#1E491A', opacity: 0.5 }}>
              {currentSection?.name} — {currentQ + 1} of {QUESTIONS.length}
            </span>
          </div>
        )}
      </header>

      {/* Progress bar */}
      <div style={{ height: '3px', background: 'rgba(0,0,0,0.08)' }}>
        <div style={{ height: '100%', width: `${progress}%`, background: '#1a1a1a', transition: 'width 0.4s ease' }} data-testid="assessment-progress-bar" />
      </div>

      {/* Main */}
      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ maxWidth: '640px', width: '100%' }}>

          {/* WELCOME SCREEN */}
          {screen === 'welcome' && (
            <div style={{ textAlign: 'center' }} data-testid="assessment-welcome">
              <div style={{ display: 'inline-block', background: '#16a34a', color: '#fff', padding: '4px 14px', borderRadius: 0, fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '16px' }}>
                STAGE 1 — FREE
              </div>
              <h1 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, color: '#1E491A', letterSpacing: '-1px', lineHeight: 1.1, marginBottom: '16px' }} data-testid="assessment-welcome-heading">
                Your Founder DNA Assessment
              </h1>
              <p style={{ fontSize: '15px', color: '#1E491A', opacity: 0.7, lineHeight: 1.7, marginBottom: '12px', maxWidth: '500px', margin: '0 auto 12px' }}>
                27 questions across 4 sections. This builds your complete Founder DNA profile and tells you exactly which pathway is yours.
              </p>
              <p style={{ fontSize: '14px', color: '#1E491A', opacity: 0.5, marginBottom: '32px' }}>
                Allow 45-90 minutes. Your progress saves automatically.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '320px', margin: '0 auto 32px' }}>
                {SECTIONS.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', background: 'rgba(255,255,255,0.5)', borderRadius: '10px', fontSize: '14px', color: '#1E491A' }}>
                    <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#1a1a1a', color: '#F6ADBC', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                    {s.name}
                  </div>
                ))}
              </div>
              <button
                onClick={() => { setScreen('question'); setCurrentQ(0); }}
                data-testid="assessment-start-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', background: '#1a1a1a', color: '#F6ADBC', border: 'none', padding: '16px 36px', borderRadius: 0, fontSize: '16px', fontWeight: 700, cursor: 'pointer' }}
              >
                {Object.keys(answers).length > 0 ? 'Continue Assessment' : 'Begin Assessment'}
                <ArrowRight style={{ width: 18, height: 18 }} />
              </button>
            </div>
          )}

          {/* QUESTION SCREEN */}
          {screen === 'question' && currentQuestion && (
            <div data-testid={`assessment-${currentQuestion.id}`}>
              {/* Section intro banner (shown on first question of each section) */}
              {isNewSection && currentSection && (
                <div style={{ background: 'rgba(255,255,255,0.5)', borderRadius: '14px', padding: '20px', marginBottom: '24px', borderLeft: '3px solid #1a1a1a' }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#1E491A', opacity: 0.4, marginBottom: '4px' }}>
                    Section {currentQuestion.section + 1} of 4
                  </p>
                  <p style={{ fontSize: '16px', fontWeight: 700, color: '#1E491A', marginBottom: '6px' }}>{currentSection.name}</p>
                  <p style={{ fontSize: '13px', color: '#1E491A', opacity: 0.6 }}>{currentSection.intro}</p>
                </div>
              )}

              {/* Back button */}
              <button onClick={handleBack} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', fontSize: '14px', color: '#1E491A', opacity: 0.5, cursor: 'pointer', marginBottom: '20px', padding: 0 }} data-testid="assessment-back-btn">
                <ArrowLeft style={{ width: 14, height: 14 }} /> Back
              </button>

              {/* Question */}
              <h2 style={{ fontSize: 'clamp(20px, 3vw, 28px)', fontWeight: 700, color: '#1E491A', lineHeight: 1.2, marginBottom: currentQuestion.subtitle ? '8px' : '6px' }}>
                {currentQuestion.question}
              </h2>
              {currentQuestion.subtitle && (
                <p style={{ fontSize: '14px', color: '#1E491A', opacity: 0.55, marginBottom: '6px', fontStyle: 'italic' }}>{currentQuestion.subtitle}</p>
              )}

              {/* Brian help */}
              {currentQuestion.brian && (
                <button
                  onClick={() => setShowBrian(!showBrian)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', fontSize: '13px', color: '#1E491A', opacity: 0.45, cursor: 'pointer', padding: '4px 0', marginBottom: '20px' }}
                  data-testid="brian-help-btn"
                >
                  <HelpCircle style={{ width: 14, height: 14 }} />
                  {showBrian ? 'Hide' : 'Brian says...'}
                </button>
              )}
              {showBrian && currentQuestion.brian && (
                <div style={{ background: 'rgba(255,255,255,0.6)', borderRadius: 0, padding: '14px 16px', marginBottom: '20px', fontSize: '14px', color: '#1E491A', opacity: 0.7, borderLeft: '3px solid #1a1a1a', lineHeight: 1.6 }} data-testid="brian-help-text">
                  {currentQuestion.brian}
                </div>
              )}
              {!showBrian && !currentQuestion.brian && <div style={{ marginBottom: '20px' }} />}

              {/* Input based on type */}
              <div style={{ marginTop: '8px' }}>
                {/* TEXT INPUT */}
                {currentQuestion.type === 'text' && (
                  <input
                    type="text"
                    value={answers[currentQuestion.id] || ''}
                    onChange={(e) => setAnswer(currentQuestion.id, e.target.value)}
                    placeholder={currentQuestion.placeholder}
                    data-testid="assessment-text-input"
                    style={{ width: '100%', padding: '14px 18px', borderRadius: '14px', border: '2px solid rgba(0,0,0,0.1)', background: 'rgba(255,255,255,0.6)', fontSize: '16px', outline: 'none', boxSizing: 'border-box' }}
                  />
                )}

                {/* TEXTAREA */}
                {currentQuestion.type === 'textarea' && (
                  <textarea
                    value={answers[currentQuestion.id] || ''}
                    onChange={(e) => setAnswer(currentQuestion.id, e.target.value)}
                    placeholder={currentQuestion.placeholder}
                    rows={5}
                    data-testid="assessment-textarea"
                    style={{ width: '100%', padding: '14px 18px', borderRadius: '14px', border: '2px solid rgba(0,0,0,0.1)', background: 'rgba(255,255,255,0.6)', fontSize: '15px', outline: 'none', resize: 'vertical', lineHeight: 1.6, boxSizing: 'border-box' }}
                  />
                )}

                {/* SINGLE SELECT */}
                {currentQuestion.type === 'single' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {currentQuestion.options.map((opt, i) => (
                      <button
                        key={i}
                        onClick={() => setAnswer(currentQuestion.id, opt.value)}
                        data-testid={`assessment-option-${i}`}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '14px',
                          padding: '14px 18px', borderRadius: '14px',
                          border: answers[currentQuestion.id] === opt.value ? '2px solid #1a1a1a' : '2px solid rgba(0,0,0,0.1)',
                          background: answers[currentQuestion.id] === opt.value ? '#1a1a1a' : 'rgba(255,255,255,0.6)',
                          color: answers[currentQuestion.id] === opt.value ? '#F6ADBC' : '#1a1a1a',
                          cursor: 'pointer', textAlign: 'left', fontSize: '15px', transition: 'all 0.2s',
                          fontWeight: answers[currentQuestion.id] === opt.value ? 600 : 400,
                        }}
                      >
                        <div style={{
                          width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
                          border: answers[currentQuestion.id] === opt.value ? '5px solid #F6ADBC' : '2px solid rgba(0,0,0,0.25)',
                        }} />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* MULTI SELECT */}
                {currentQuestion.type === 'multi' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {currentQuestion.options.map((opt, i) => {
                      const selected = (answers[currentQuestion.id] || []).includes(opt.value);
                      return (
                        <button
                          key={i}
                          onClick={() => {
                            const curr = answers[currentQuestion.id] || [];
                            const next = selected ? curr.filter(v => v !== opt.value) : [...curr, opt.value];
                            setAnswer(currentQuestion.id, next);
                          }}
                          data-testid={`assessment-multi-${i}`}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '14px',
                            padding: '14px 18px', borderRadius: '14px',
                            border: selected ? '2px solid #1a1a1a' : '2px solid rgba(0,0,0,0.1)',
                            background: selected ? '#1a1a1a' : 'rgba(255,255,255,0.6)',
                            color: selected ? '#F6ADBC' : '#1a1a1a',
                            cursor: 'pointer', textAlign: 'left', fontSize: '15px', transition: 'all 0.2s',
                          }}
                        >
                          <div style={{
                            width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0,
                            border: selected ? '2px solid #F6ADBC' : '2px solid rgba(0,0,0,0.25)',
                            background: selected ? '#F6ADBC' : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {selected && <CheckCircle2 style={{ width: 12, height: 12, color: '#1E491A' }} />}
                          </div>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* SCALE 1-5 */}
                {currentQuestion.type === 'scale5' && (
                  <div>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      {[1, 2, 3, 4, 5].map(n => (
                        <button
                          key={n}
                          onClick={() => setAnswer(currentQuestion.id, n)}
                          data-testid={`assessment-scale-${n}`}
                          style={{
                            flex: 1, padding: '16px 0', borderRadius: '14px',
                            border: answers[currentQuestion.id] === n ? '2px solid #1a1a1a' : '2px solid rgba(0,0,0,0.1)',
                            background: answers[currentQuestion.id] === n ? '#1a1a1a' : 'rgba(255,255,255,0.6)',
                            color: answers[currentQuestion.id] === n ? '#F6ADBC' : '#1a1a1a',
                            fontSize: '18px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
                          }}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                    {currentQuestion.scaleLabels && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#1E491A', opacity: 0.4, padding: '0 4px' }}>
                        <span>{currentQuestion.scaleLabels[1]}</span>
                        <span>{currentQuestion.scaleLabels[5]}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* SCALE 1-10 */}
                {currentQuestion.type === 'scale10' && (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: '4px', marginBottom: '8px' }}>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                        <button
                          key={n}
                          onClick={() => setAnswer(currentQuestion.id, n)}
                          data-testid={`assessment-scale10-${n}`}
                          style={{
                            padding: '14px 0', borderRadius: '10px',
                            border: answers[currentQuestion.id] === n ? '2px solid #1a1a1a' : '2px solid rgba(0,0,0,0.1)',
                            background: answers[currentQuestion.id] === n ? '#1a1a1a' : 'rgba(255,255,255,0.6)',
                            color: answers[currentQuestion.id] === n ? '#F6ADBC' : '#1a1a1a',
                            fontSize: '16px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s',
                          }}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                    {currentQuestion.scaleLabels && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#1E491A', opacity: 0.4, padding: '0 4px' }}>
                        <span>{currentQuestion.scaleLabels[1]}</span>
                        <span>{currentQuestion.scaleLabels[10]}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* CURRENCY INPUT */}
                {currentQuestion.type === 'currency' && (
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', fontSize: '18px', color: '#1E491A', opacity: 0.4, fontWeight: 600 }}>$</span>
                    <input
                      type="number"
                      value={answers[currentQuestion.id] || ''}
                      onChange={(e) => setAnswer(currentQuestion.id, e.target.value)}
                      placeholder={currentQuestion.placeholder}
                      min="0"
                      data-testid="assessment-currency-input"
                      style={{ width: '100%', padding: '14px 18px 14px 36px', borderRadius: '14px', border: '2px solid rgba(0,0,0,0.1)', background: 'rgba(255,255,255,0.6)', fontSize: '18px', fontWeight: 600, outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                )}

                {/* HOUSING (Q19) */}
                {currentQuestion.type === 'housing' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {[
                      { value: 'mortgage', label: 'Yes, mortgage' },
                      { value: 'rent', label: 'Yes, rent' },
                      { value: 'none', label: 'No housing payment' },
                    ].map((opt, i) => {
                      const selected = answers[`${currentQuestion.id}_type`] === opt.value;
                      return (
                        <div key={i}>
                          <button
                            onClick={() => {
                              setAnswer(`${currentQuestion.id}_type`, opt.value);
                              if (opt.value === 'none') setAnswer(`${currentQuestion.id}_amount`, '0');
                            }}
                            data-testid={`assessment-housing-${opt.value}`}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '14px', width: '100%',
                              padding: '14px 18px', borderRadius: '14px',
                              border: selected ? '2px solid #1a1a1a' : '2px solid rgba(0,0,0,0.1)',
                              background: selected ? '#1a1a1a' : 'rgba(255,255,255,0.6)',
                              color: selected ? '#F6ADBC' : '#1a1a1a', cursor: 'pointer', textAlign: 'left', fontSize: '15px',
                            }}
                          >
                            <div style={{
                              width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
                              border: selected ? '5px solid #F6ADBC' : '2px solid rgba(0,0,0,0.25)',
                            }} />
                            {opt.label}
                          </button>
                          {selected && opt.value !== 'none' && (
                            <div style={{ marginTop: '8px', marginLeft: '32px', position: 'relative' }}>
                              <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#1E491A', opacity: 0.4 }}>$</span>
                              <input
                                type="number"
                                value={answers[`${currentQuestion.id}_amount`] || ''}
                                onChange={(e) => setAnswer(`${currentQuestion.id}_amount`, e.target.value)}
                                placeholder="Monthly amount"
                                min="0"
                                data-testid="assessment-housing-amount"
                                style={{ width: '200px', padding: '10px 14px 10px 32px', borderRadius: '10px', border: '2px solid rgba(0,0,0,0.1)', background: 'rgba(255,255,255,0.6)', fontSize: '16px', fontWeight: 600, outline: 'none' }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* SKILL GRID (Q25) */}
                {currentQuestion.type === 'skill_grid' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {currentQuestion.skills.map((skill, i) => {
                      const val = (answers[currentQuestion.id] || {})[skill] || 0;
                      return (
                        <div key={i} style={{ background: 'rgba(255,255,255,0.5)', borderRadius: '14px', padding: '16px' }}>
                          <p style={{ fontSize: '14px', fontWeight: 600, color: '#1E491A', marginBottom: '10px' }}>{skill}</p>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            {[1, 2, 3, 4, 5].map(n => (
                              <button
                                key={n}
                                onClick={() => {
                                  const grid = { ...(answers[currentQuestion.id] || {}), [skill]: n };
                                  setAnswer(currentQuestion.id, grid);
                                }}
                                data-testid={`skill-${i}-${n}`}
                                style={{
                                  flex: 1, padding: '10px 0', borderRadius: '10px',
                                  border: val === n ? '2px solid #1a1a1a' : '2px solid rgba(0,0,0,0.1)',
                                  background: val === n ? '#1a1a1a' : 'transparent',
                                  color: val === n ? '#F6ADBC' : '#1a1a1a',
                                  fontSize: '14px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                                }}
                              >
                                {n}
                              </button>
                            ))}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#1E491A', opacity: 0.35, marginTop: '4px', padding: '0 2px' }}>
                            <span>Beginner</span><span>Expert</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Financial Calculator (show after Q17 during financial section) */}
                {currentQuestion.section === 2 && currentQ >= QUESTIONS.findIndex(q => q.id === 'q17') && (
                  <FinancialCalculator answers={answers} />
                )}
              </div>

              {/* Next button */}
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                data-testid="assessment-next-btn"
                style={{
                  marginTop: '28px',
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  background: canProceed() ? '#1a1a1a' : 'rgba(0,0,0,0.15)',
                  color: canProceed() ? '#F6ADBC' : 'rgba(0,0,0,0.3)',
                  border: 'none', padding: '14px 32px', borderRadius: 0,
                  fontSize: '15px', fontWeight: 600,
                  cursor: canProceed() ? 'pointer' : 'not-allowed', transition: 'all 0.2s',
                }}
              >
                {currentQ === QUESTIONS.length - 1 ? 'Complete Assessment' : 'Continue'}
                <ArrowRight style={{ width: 16, height: 16 }} />
              </button>
            </div>
          )}

          {/* PROCESSING SCREEN */}
          {screen === 'processing' && (
            <div style={{ textAlign: 'center' }} data-testid="assessment-processing">
              <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#1E491A', marginBottom: '32px' }}>
                Building your Founder DNA profile...
              </h2>
              <div style={{ maxWidth: '400px', margin: '0 auto' }}>
                {processingSteps.map((step, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '12px 0',
                      opacity: i <= processingStep ? 1 : 0.25,
                      transition: 'opacity 0.5s',
                    }}
                  >
                    {i < processingStep ? (
                      <CheckCircle2 style={{ width: 18, height: 18, color: '#16a34a', flexShrink: 0 }} />
                    ) : i === processingStep ? (
                      <Loader2 style={{ width: 18, height: 18, color: '#1E491A', flexShrink: 0 }} className="animate-spin" />
                    ) : (
                      <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />
                    )}
                    <span style={{ fontSize: '14px', color: '#1E491A', fontWeight: i <= processingStep ? 600 : 400 }}>
                      {step}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* DONE — Redirect to results */}
          {screen === 'done' && (
            <div style={{ textAlign: 'center' }} data-testid="assessment-done">
              <CheckCircle2 style={{ width: 48, height: 48, color: '#16a34a', margin: '0 auto 16px' }} />
              <h2 style={{ fontSize: '28px', fontWeight: 700, color: '#1E491A', marginBottom: '12px' }}>
                Assessment Complete
              </h2>
              <p style={{ fontSize: '15px', color: '#1E491A', opacity: 0.7, marginBottom: '28px' }}>
                Your Founder DNA profile is ready.
              </p>
              <button
                onClick={() => navigate('/results')}
                data-testid="assessment-view-results-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', background: '#1a1a1a', color: '#F6ADBC', border: 'none', padding: '16px 36px', borderRadius: 0, fontSize: '16px', fontWeight: 700, cursor: 'pointer' }}
              >
                See My Results
                <ArrowRight style={{ width: 18, height: 18 }} />
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Stage1Assessment;
